import { useState, useEffect, useCallback, useRef } from 'react';
import { collectionGroup, collection, doc, setDoc, getDocs, deleteDoc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { CRITERIA } from '../constants';
import { logMonitoringEvent } from '../utils/monitoringLogger';

const WRITE_DEBOUNCE_MS = 300;

/**
 * Manages scores for a single contest with real-time updates via a single
 * collection group query instead of per-contestant listeners.
 * allScores shape: { [contestantId]: { [userId]: scoreData } }
 */
export const useScores = (contestId, user) => {
  const [allScores, setAllScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastChanged, setLastChanged] = useState(null);
  const unsubRef = useRef(null);
  const writeTimers = useRef({});

  useEffect(() => {
    if (!contestId || !user) { setLoading(false); return; }
    setLoading(true);

    // Reset stale state from previous contest before attaching new listener
    setAllScores({});
    setLastChanged(null);

    // Clean up prior listener
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    // Single collection group query filtered by contestId
    const scoresQuery = query(
      collectionGroup(db, 'scores'),
      where('contestId', '==', contestId)
    );

    const unsub = onSnapshot(scoresQuery, (snap) => {
      const changes = snap.docChanges();

      // On first load (no prior state) process all docs; on updates process only deltas
      if (snap.metadata.fromCache === false && changes.length === snap.size && snap.size > 0 && changes.every(c => c.type === 'added')) {
        // Initial load — build full map once
        const initial = {};
        snap.forEach((sDoc) => {
          const data = sDoc.data();
          const contestantId = sDoc.ref.parent.parent.id;
          if (!initial[contestantId]) initial[contestantId] = {};
          if (data.overall === undefined) {
            let total = 0, hasAll = true;
            CRITERIA.forEach(c => {
              if (data[c.id] !== undefined) total += parseFloat(data[c.id]) * c.weight;
              else hasAll = false;
            });
            if (hasAll) data.overall = total;
          }
          initial[contestantId][sDoc.id] = data;
        });
        setAllScores(initial);
        setLoading(false);
        return;
      }

      // Incremental update — only process changed docs
      if (changes.length === 0) { setLoading(false); return; }

      setAllScores(prev => {
        const next = { ...prev };
        let firstChangedContestant = null;
        let lastVoterId = null;
        let lastOverall = null;

        changes.forEach(({ type, doc: sDoc }) => {
          const contestantId = sDoc.ref.parent.parent.id;
          if (!firstChangedContestant) firstChangedContestant = contestantId;

          if (type === 'removed') {
            if (next[contestantId]) {
              const { [sDoc.id]: _, ...rest } = next[contestantId];
              next[contestantId] = rest;
            }
          } else {
            const data = sDoc.data();
            if (data.overall === undefined) {
              let total = 0, hasAll = true;
              CRITERIA.forEach(c => {
                if (data[c.id] !== undefined) total += parseFloat(data[c.id]) * c.weight;
                else hasAll = false;
              });
              if (hasAll) data.overall = total;
            }
            next[contestantId] = { ...(next[contestantId] || {}), [sDoc.id]: data };
            lastVoterId = sDoc.id;
            if (typeof data.overall === 'number') lastOverall = data.overall;
          }
        });

        if (firstChangedContestant) {
          setLastChanged({
            contestantId: firstChangedContestant,
            voterId: lastVoterId,
            overall: lastOverall,
            timestamp: Date.now(),
          });
        }
        return next;
      });

      setLoading(false);
    });

    unsubRef.current = unsub;

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [contestId, user]);

  const getScore = useCallback((contestantId, criterionId) => {
    if (!user) return 0;
    return allScores[contestantId.toString()]?.[user.id]?.[criterionId] || 0;
  }, [allScores, user]);

  const submitScore = useCallback(async (contestantId, criterionId, value) => {
    if (!user || !contestId) return;
    const key = contestantId.toString();

    let dataToWrite;
    setAllScores(prev => {
      const current = { ...(prev[key]?.[user.id] || {}) };
      current[criterionId] = parseFloat(value) || 0;
      current.voterName = user.name;
      current.voterPhotoURL = user.photoURL || null;
      current.voterIsAdmin = user.isAdmin;
      current.contestId = contestId;

      let overall = 0;
      let hasAll = true;
      CRITERIA.forEach(c => {
        if (current[c.id] !== undefined) {
          overall += parseFloat(current[c.id]) * c.weight;
        } else { hasAll = false; }
      });
      if (hasAll) current.overall = overall;
      else delete current.overall;

      dataToWrite = current;
      return { ...prev, [key]: { ...(prev[key] || {}), [user.id]: current } };
    });

    // Debounce: collapse rapid taps into one write per contestant
    const timerKey = `${key}`;
    if (writeTimers.current[timerKey]) clearTimeout(writeTimers.current[timerKey]);
    writeTimers.current[timerKey] = setTimeout(async () => {
      try {
        const scoreRef = doc(db, "contests", contestId, "contestants", key, "scores", user.id);
        await setDoc(scoreRef, { ...dataToWrite, updatedAt: new Date().toISOString() }, { merge: true });
      } catch (error) {
        console.error("Error updating score:", error);
        logMonitoringEvent('error', 'firestore', `Failed to submit score: ${error.message}`, {
          userId: user?.id, userName: user?.name,
          metadata: { contestantId: key, criterionId },
        });
      }
    }, WRITE_DEBOUNCE_MS);
  }, [user, contestId]);

  const deleteScore = useCallback(async (userId, contestantId) => {
    if (!user?.isAdmin || !contestId) return;
    const key = contestantId.toString();
    await deleteDoc(doc(db, "contests", contestId, "contestants", key, "scores", userId));
    setAllScores(prev => {
      const next = { ...prev };
      if (next[key]?.[userId]) {
        const { [userId]: _, ...rest } = next[key];
        next[key] = rest;
      }
      return next;
    });
  }, [user, contestId]);

  const clearAllScores = useCallback(async () => {
    if (!user?.isAdmin || !contestId) return;
    const contestantsRef = collection(db, "contests", contestId, "contestants");
    const contestantsSnapshot = await getDocs(contestantsRef);
    for (const cDoc of contestantsSnapshot.docs) {
      const scoresRef = collection(db, "contests", contestId, "contestants", cDoc.id, "scores");
      const scoresSnapshot = await getDocs(scoresRef);
      for (const sDoc of scoresSnapshot.docs) {
        await deleteDoc(sDoc.ref);
      }
    }
    setAllScores({});
  }, [user, contestId]);

  return {
    allScores, loading, lastChanged, getScore, submitScore,
    deleteScore, clearAllScores,
  };
};
